const CURRENCY = "ZAR"
const PURCHASE_STORAGE_PREFIX = "eurobath_purchase_"
const trackedPurchases = new Set()

const isBrowser = () => typeof window !== "undefined"

const amount = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0
  const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""))
  return Number.isFinite(parsed) ? parsed : 0
}

const categoriesFor = (item = {}) => {
  const categories =
    item.categories?.nodes || item.productCategories?.nodes || item.categories || []
  return Array.isArray(categories)
    ? categories.map((category) => category?.name || category?.slug).filter(Boolean).join("/")
    : undefined
}

const productPrice = (item = {}) => {
  const quantity = amount(item.quantity) || 1
  if (item.unit_price ?? item.price_each ?? item.price) {
    return amount(item.unit_price ?? item.price_each ?? item.price)
  }
  return amount(item.line_total) / quantity
}

export const ecommerceItem = (item = {}, index) => {
  const result = {
    item_id:
      item.tracking_sku ||
      item.product_sku ||
      item.sku ||
      item.variation_sku ||
      item.variationId ||
      item.product_id ||
      item.productId ||
      item.id,
    item_name: item.name || item.product_name,
    item_brand: item.item_brand || item.brand || "Euro Bath",
    item_category: categoriesFor(item),
    item_variant:
      item.item_variant ||
      item.variant ||
      item.attributes?.nodes?.map((attribute) => attribute?.value).filter(Boolean).join(" / "),
    price: productPrice(item),
    quantity: amount(item.quantity) || 1,
  }

  if (typeof index === "number") result.index = index
  return Object.fromEntries(
    Object.entries(result).filter(([, value]) => value !== undefined && value !== "")
  )
}

export const ecommerceItems = (items = []) =>
  (items || [])
    .filter((item) => !item?.bundled_by)
    .map((item, index) => ecommerceItem(item, index))

const eventValue = (items) =>
  items.reduce((total, item) => total + item.price * item.quantity, 0)

// Clear ecommerce first so a previous event's values cannot leak into the next GTM event.
export const pushDataLayerEvent = (event, payload = {}) => {
  if (!isBrowser()) return

  window.dataLayer = window.dataLayer || []
  if (payload.ecommerce) window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({ event, ...payload })

  if (process.env.GATSBY_GTAG_DEBUG === "true") {
    console.log("dataLayer event:", { event, ...payload })
  }
}

export const trackEcommerce = (event, sourceItems = [], options = {}) => {
  const items = ecommerceItems(sourceItems)
  const ecommerce = {
    currency: CURRENCY,
    value: options.value === undefined ? eventValue(items) : amount(options.value),
    items,
    ...options.ecommerce,
  }
  pushDataLayerEvent(event, { ecommerce })
}

export const trackQuoteStarted = () =>
  pushDataLayerEvent("quote_started", { lead_type: "Request For Quote" })

export const trackQuoteSubmitted = (order = {}) =>
  pushDataLayerEvent("quote_submitted", {
    lead_type: "Request For Quote",
    quote_reference: String(order.number || order.id || ""),
  })

export const transactionEcommerce = (order = {}) => {
  const items = ecommerceItems(order.line_items)
  return {
    transaction_id: String(order.number || order.id || ""),
    affiliation: process.env.GATSBY_WEBSITE_TITLE || "Euro Bath",
    currency: CURRENCY,
    value: amount(order.total ?? order.total_price ?? eventValue(items)),
    tax: amount(order.total_tax ?? order.tax),
    shipping: amount(order.shipping_total ?? order.shipping),
    coupon: Array.isArray(order.coupon_lines)
      ? order.coupon_lines.map((coupon) => coupon.code).filter(Boolean).join(",")
      : order.coupon_code,
    payment_type: order.payment_method_title || order.payment_method,
    shipping_tier: order.shipping_lines?.[0]?.method_title || order.shipping_method,
    items,
  }
}

// A completed-order page can be revisited or refreshed. Store the transaction
// ID locally so a valid purchase is represented by one data-layer event per
// browser, while keeping the GA4 transaction ID available as a second guard.
export const trackPurchaseOnce = (order = {}) => {
  const ecommerce = transactionEcommerce(order)
  const transactionId = ecommerce.transaction_id

  if (!isBrowser() || !transactionId || trackedPurchases.has(transactionId)) {
    return false
  }

  const storageKey = `${PURCHASE_STORAGE_PREFIX}${transactionId}`

  try {
    if (window.localStorage.getItem(storageKey)) return false
  } catch {
    // Private browsing or storage restrictions should not prevent valid tracking.
  }

  pushDataLayerEvent("purchase", { ecommerce })
  trackedPurchases.add(transactionId)

  try {
    window.localStorage.setItem(storageKey, "1")
  } catch {
    // The in-memory guard still prevents duplicate pushes in the current page.
  }

  return true
}

export { CURRENCY, amount }
